import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import html2canvas from "html2canvas";
import { Rnd } from "react-rnd"; // ✅ Draggable, Resizable text
import "./css/editor.css";

const Editor = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [userEmail, setUserEmail] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 500, height: 500 });
  const [texts, setTexts] = useState([]);
  const [filter, setFilter] = useState("none");

  const editorRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email.replace(/\./g, "_"));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userEmail) {
      fetchImages();
    }
  }, [userEmail]);

  const fetchImages = async () => {
    try {
      const imagesSnapshot = await getDocs(collection(db, `users/${userEmail}/uploadedFiles`));
      const imagesList = imagesSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((file) => file.type.startsWith("image/"));
      setUploadedImages(imagesList);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setUploadedImages([...uploadedImages, { id: Date.now(), url: imageUrl }]);
      selectImage(imageUrl);
    }
  };

  const selectImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    setTexts([]);
    setFilter("none");

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      let maxWidth = 500;
      let scaleFactor = maxWidth / img.width;
      let newHeight = img.height * scaleFactor;
      setImageSize({ width: maxWidth, height: newHeight });
    };
  };

  const addText = () => {
    setTexts([
      ...texts,
      {
        id: Date.now(),
        text: "New Text",
        color: "#ffffff",
        size: 30,
        fontFamily: "Arial",
        textAlign: "center",
        position: { x: 150, y: 100 },
        width: 200,
        height: 50,
      },
    ]);
  };

  const updateText = (id, key, value) => {
    setTexts((prevTexts) =>
      prevTexts.map((text) =>
        text.id === id
          ? {
              ...text,
              [key]: value,
              position:
                key === "textAlign"
                  ? {
                      ...text.position,
                      x:
                        value === "center"
                          ? imageSize.width / 2 - text.width / 2 // Center it
                          : value === "right"
                          ? imageSize.width - text.width - 10 // Right-align with margin
                          : 10, // Left-align with margin
                    }
                  : key === "verticalAlign"
                  ? {
                      ...text.position,
                      y: value === "up" ? text.position.y - 10 : text.position.y + 10, // Move Up or Down
                    }
                  : text.position,
            }
          : text
      )
    );
  };

  const downloadImage = () => {
    if (editorRef.current) {
      html2canvas(editorRef.current).then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = "edited-image.png";
        link.click();
      });
    }
  };

  return (
    <div className="editor-page">
      <aside className="sidebar left-sidebar">
        <h3>📂 Your Images</h3>
        <input type="file" accept="image/*" onChange={handleImageUpload} />
        <div className="image-list">
          {uploadedImages.map((image) => (
            <img key={image.id} src={image.url} alt="Uploaded" className="sidebar-img" onClick={() => selectImage(image.url)} />
          ))}
        </div>
      </aside>

      <div className="editor-container">
        <h2>🎨 Image Editor</h2>

        {selectedImage && (
          <div ref={editorRef} className="image-preview" style={{ width: `${imageSize.width}px`, height: `${imageSize.height}px`, position: "relative", filter }}>
            <img src={selectedImage} alt="Preview" style={{ width: "100%", height: "100%" }} />
            {texts.map(({ id, text, color, size, fontFamily, textAlign, position, width, height }) => (
              <Rnd
                key={id}
                size={{ width, height }}
                position={{ x: position.x, y: position.y }}
                onDragStop={(e, d) => updateText(id, "position", { x: d.x, y: d.y })}
              >
                <div className="text-box" style={{ fontSize: `${size}px`, color, fontFamily, textAlign }}>
                  {text}
                </div>
              </Rnd>
            ))}
          </div>
        )}
      </div>

      {selectedImage && (
        <aside className="sidebar right-sidebar">
          <h3>✍️ Add & Edit Text</h3>
          <button onClick={addText}>➕ Add Text</button>
          <button className="remove-text-btn" onClick={() => setTexts([])}>❌ Remove All Text</button>

          {texts.map(({ id, text, color, size, fontFamily, textAlign }) => (
            <div key={id} className="text-controls">
              <input type="text" value={text} onChange={(e) => updateText(id, "text", e.target.value)} />
              <input type="color" value={color} onChange={(e) => updateText(id, "color", e.target.value)} />
              <input type="range" min="10" max="100" value={size} onChange={(e) => updateText(id, "size", e.target.value)} />
              <select 
          value={fontFamily} 
          onChange={(e) => updateText(id, "fontFamily", e.target.value)}
        >
          <option>Arial</option>
          <option>Courier New</option>
          <option>Georgia</option>
          <option>Times New Roman</option>
          <option>Verdana</option>
          <option>Comic Sans MS</option>
          <option>Impact</option>
        </select>

              <div className="alignment-controls">
                <button onClick={() => updateText(id, "textAlign", "left")}>⬅ Left</button>
                <button onClick={() => updateText(id, "textAlign", "center")}>🔳 Center</button>
                <button onClick={() => updateText(id, "textAlign", "right")}>➡ Right</button>
                <button onClick={() => updateText(id, "verticalAlign", "up")}>⬆ Up</button>
                <button onClick={() => updateText(id, "verticalAlign", "down")}>⬇ Down</button>
              </div>
            </div>
          ))}

<h3>🎭 Image Effects</h3>
    <div className="effects-buttons">
      <button className="effect-btn reset" onClick={() => setFilter("none")}>🔄 Reset</button>
      <button className="effect-btn grayscale" onClick={() => setFilter("grayscale(100%)")}>⚫ Grayscale</button>
      <button className="effect-btn sepia" onClick={() => setFilter("sepia(100%)")}>🟤 Sepia</button>
      <button className="effect-btn invert" onClick={() => setFilter("invert(100%)")}>🎨 Invert</button>
      <button className="effect-btn contrast" onClick={() => setFilter("contrast(200%)")}>🌟 High Contrast</button>
      <button className="effect-btn blur" onClick={() => setFilter("blur(3px)")}>💨 Blur</button>
      <button className="effect-btn brightness" onClick={() => setFilter("brightness(150%)")}>☀️ Brightness</button>
    </div>

          <h3>📥 Download & Reset</h3>
          <button onClick={downloadImage}>📥 Download Image</button>
        </aside>
      )}
    </div>
  );
};

export default Editor;
